import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Users, Trash2, ShieldCheck, Search, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import {
  listRecentValidations,
  groupValidations,
  revokeValidation,
  forceDiffusionStatus,
  ValidationRow,
  ValidationGroup,
} from '../services/validations';

export function ValidationsPage() {
  const [rows, setRows] = useState<ValidationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listRecentValidations());
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const channel = supabase
      .channel('validations-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bar_validations' }, () => reload())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [reload]);

  const groups = useMemo(() => groupValidations(rows), [rows]);

  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const s = search.toLowerCase();
    return groups.filter(
      g =>
        g.bar_name.toLowerCase().includes(s) ||
        g.match_label.toLowerCase().includes(s) ||
        g.competition.toLowerCase().includes(s)
    );
  }, [groups, search]);

  const totalVotes = rows.length;
  const totalGroups = groups.length;

  const handleRevoke = async (vote: ValidationRow) => {
    if (!window.confirm('Révoquer ce vote ? Cette action est loguée.')) return;
    try {
      await toast.promise(revokeValidation(vote), {
        loading: 'Révocation…',
        success: 'Vote révoqué',
        error: 'Erreur — vérifie les permissions',
      });
    } catch {
      // toast already showed
    }
  };

  const handleForce = async (
    g: ValidationGroup,
    status: 'confirmed' | 'probable' | 'unconfirmed'
  ) => {
    const labels: Record<string, string> = { unconfirmed: 'non vérifié', probable: 'probable', confirmed: 'confirmé' };
    if (!window.confirm(`Forcer ${g.bar_name} en "${labels[status]}" pour ${g.match_label} ?`)) return;
    try {
      await toast.promise(
        forceDiffusionStatus(g.bar_id, g.match_id, status, {
          barName: g.bar_name,
          matchLabel: g.match_label,
        }),
        {
          loading: 'Sauvegarde…',
          success: `Statut forcé : ${labels[status]}`,
          error: 'Erreur — vérifie les permissions',
        }
      );
    } catch {
      // toast already showed
    }
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <Users size={20} />
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Validations communautaires</h1>
      </div>
      <p className="muted" style={{ marginBottom: '20px', fontSize: '13px' }}>
        Tous les votes ONpitchers sur les matchs à venir. Tu peux révoquer un vote ou forcer le statut d'une diffusion.
      </p>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
        <span><b style={{ color: 'var(--text-primary)' }}>{totalVotes}</b> vote{totalVotes > 1 ? 's' : ''}</span>
        <span><b style={{ color: 'var(--text-primary)' }}>{totalGroups}</b> couple{totalGroups > 1 ? 's' : ''} bar × match</span>
      </div>

      <div className="filter-search" style={{ marginBottom: '16px', maxWidth: '380px' }}>
        <Search size={14} />
        <input
          placeholder="Rechercher un bar, match ou compétition…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {error && (
        <div className="card" style={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'var(--red)', marginBottom: '12px' }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="skeleton-list">{[1, 2, 3, 4].map(i => <div key={i} className="skeleton-row" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <p className="muted">Aucun vote pour l'instant</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.map((g, idx) => {
            const key = `${g.bar_id}__${g.match_id}`;
            const isOpen = expanded.has(key);
            return (
              <div
                key={key}
                style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}
              >
                <div
                  onClick={() => toggleExpand(key)}
                  style={{
                    padding: '14px 18px',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    transition: 'background 0.1s',
                  }}
                >
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>
                      {g.bar_name} <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>·</span> {g.match_label}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {g.match_date && format(new Date(g.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })}
                      {g.match_time && ` · ${g.match_time}`}
                      {g.competition && ` · ${g.competition}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', fontWeight: 700 }}>
                    <span style={{ color: 'var(--green)' }}>+{g.confirmations}</span>
                    <span style={{ color: 'var(--red)' }}>−{g.denials}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleForce(g, 'probable')}
                      style={{ fontSize: '11px', padding: '4px 8px' }}
                      title="Forcer en probable"
                    >
                      Probable
                    </button>
                    <button
                      className="btn btn-green"
                      onClick={() => handleForce(g, 'confirmed')}
                      style={{ fontSize: '11px', padding: '4px 8px' }}
                      title="Forcer en confirmé"
                    >
                      <ShieldCheck size={12} /> Forcer
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ background: 'var(--bg-secondary)', padding: '8px 18px 14px 42px' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '11px' }}>Type</th>
                          <th style={{ fontSize: '11px' }}>Utilisateur</th>
                          <th style={{ fontSize: '11px' }}>Date</th>
                          <th style={{ textAlign: 'right', fontSize: '11px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.votes.map(v => (
                          <tr key={v.id}>
                            <td>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: v.validation_type === 'confirming' ? 'var(--green)' : 'var(--red)',
                                }}
                              >
                                {v.validation_type === 'confirming' ? 'Ça diffuse' : 'Ça diffuse pas'}
                              </span>
                            </td>
                            <td style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                              {v.user_id ? v.user_id.substring(0, 8) + '…' : '-'}
                            </td>
                            <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {format(new Date(v.created_at), 'dd/MM HH\'h\'mm', { locale: fr })}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                className="btn-icon danger"
                                onClick={() => handleRevoke(v)}
                                title="Révoquer ce vote"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
